import { useContext } from "react";

import ListGroup from "react-bootstrap/ListGroup";

import { Skeleton, Alert, Button, ModalContext } from "@evos/components";
import ErrorListGroupItem from "components/ErrorListGroupItem";

import { Link, useNavigate } from "react-router-dom";

import type { 
  ChargerStatusMessageNode, 
  ServiceLogsNode 
} from "types";

type NotifcationsListProps = {
  errorData: ChargerStatusMessageNode[] | ServiceLogsNode[] | undefined,
  loading: boolean,
  darkMode: boolean,
  emptyStateMessage?: string,
}

export default function NotificationsList({
  errorData, 
  loading, 
  darkMode,
  emptyStateMessage = "No status messages in the last hour",
}: NotifcationsListProps) {

  const navigate = useNavigate();

  const { handleModal } = useContext(ModalContext);

  const openModal = (index: number) => {
    if ((errorData != null) && !loading && isServiceErrorData(errorData)) {
      handleModal({
        title: `${errorData[index].code} in ${errorData[index].container_name}`,
        content: <>
          <p><b>Description: </b>{errorData[index].description}</p>
          <span>
            <b>Context: </b>
            <pre>{JSON.stringify(JSON.parse(errorData[index].context), null, 2)}</pre>
          </span>
        </>,
        size: "lg",
        cancelButtonText: "Close",
      });
    }
  };

  // Type check for errorData
  const isServiceErrorData = (
    errorData: ServiceLogsNode[] | ChargerStatusMessageNode[]  | undefined
  ): errorData is ServiceLogsNode[] => {
    if (errorData == null || errorData?.length === 0) {
      return false;
    }
    return "container_name" in errorData![0];
  };

  return (
    <>
      { !loading
        ? (errorData?.length ?? 0) > 0
          ? <ListGroup variant="flush">
            { errorData?.map((e, index) => (
              "container_name" in e
              // eslint-disable-next-line react/no-array-index-key
                ? <div key={`${e.receive_timestamp}-${index}`} onClick={() => openModal(index)}>
                  <ErrorListGroupItem
                    severity={e.severity}
                    code={e.code}
                    containerName={e.container_name}
                    timestamp={e.receive_timestamp}
                    description={e.description}
                    darkMode={darkMode!}
                  />
                </div>
                // eslint-disable-next-line react/no-array-index-key
                : <div key={`${e.receiveTimestamp}-${index}`} onClick={() => navigate(`/charger-logs-list?search=${e.serial}`)}>
                  <ErrorListGroupItem
                    severity="ERROR"
                    code={e.errorCode === "OtherError" ? `${e.errorCode} (${e.vendorErrorCode})` : e.errorCode}
                    containerName={e.serial}
                    timestamp={e.receiveTimestamp}
                    description={`Status: ${e.status}`}
                    darkMode={darkMode!}
                  />
                </div>
            ))
            }
          </ListGroup>
          : <Alert variant="info" className="mt-3 mx-3 text-center" message={emptyStateMessage} />
        : <Skeleton
          width="100%"
          height="120px"
          outerClassName="d-flex p-2"
          innerClassName="mb-1"
          lines={3}
        />
      }
      { (errorData != null) && !loading 
        && <div className="d-flex justify-content-center">
          { isServiceErrorData(errorData)
            ? <Link to="/service-error-list" className="py-2"><Button className="my-2">View all errors</Button></Link>
            : <Link to="/charger-logs-list" className="py-2"><Button className="my-2">View all messages</Button></Link>
          }
        </div>
      }
    </>
  );
}